import React, { Fragment, useState } from 'react';
import { Alert, Card as BSCard, Col, Row, Form, Button } from 'reactstrap';
import { Card } from '../base/components/CardAccordion';
import Editor3ColLayout, { LeftSidebar, MainPane, RightSidebar } from '../base/components/Editor3ColLayout';
// import { FCard, FilterForm, FPropControl } from '../base/components/FilterUI';
import { DefaultListItem } from '../base/components/ListLoad';
import Misc from '../base/components/Misc';
import PropControl from '../base/components/PropControl';
import SavePublishDeleteEtc from '../base/components/SavePublishDeleteEtc';
import StyleBlock from '../base/components/StyleBlock';
import { setTaskTags } from '../base/components/TaskList';
import Wizard, { WizardStage } from '../base/components/WizardProgressWidget';
import Advert, { KAdFormat } from '../base/data/Advert';
import KStatus from '../base/data/KStatus';
import XId from '../base/data/XId';
import { getDataItem, setWindowTitle } from '../base/plumbing/Crud';
import DataStore from '../base/plumbing/DataStore';
import Roles from '../base/Roles';
import { assert } from '../base/utils/assert';
import { getScreenSize, asNum, isDebug } from '../base/utils/miscutils';
import C from '../C';
import ActionMan from '../plumbing/ActionMan';
import AdDesignControls from './AdDesignControls';
import AdDesignExtraControls from './AdDesignExtraControls';
import AdPreviewCard from './AdPreviewCard';
import { AdvertWizardChecklist } from './Checklist';
import EndCardDesignControls from './EndCardDesignControls';
import ExportControls from './ExportControls';
import GoalControls, { isStartComplete } from './GoalControls';
import ListItems from './ListItems';
import KWorkflow, { titleForWorkflow, WORKFLOW_STEPS } from './KWorkflow';
import { getId, getStatus, getType } from '../base/data/DataClass';
import { getNavContext, setNavContext } from '../base/components/NavBar';
import SearchQuery from '../base/searchquery';
import LinkOut from '../base/components/LinkOut';
import CopyButton from '../base/components/CopyButton';
import { dateStr, dateUTCfromString } from '../base/utils/date-utils';


function AdvertPage() {
	const type = C.TYPES.Advert;
	setWindowTitle(type);

	const blocked = Misc.CheckAccess({ can: 'editAdvert' });
	if (blocked) return blocked;

	const path = DataStore.getValue(['location', 'path']);
	const advertId = path[1];

	setNavContext(C.TYPES.Advert, null);

	// No ID? Show the list.
	if ( ! advertId) {
		setTaskTags(C.TYPES.Advert);
		return <ListAdverts />;
	}

	const item = ActionMan.getDataItem({ type, id: advertId, status: KStatus.DRAFT }).value;
	// Advert specified, but not loaded yet.
	if (!item) {
		return <><h1>Advert: {advertId}</h1><Misc.Loading /></>;
	}

	setWindowTitle(item);

	// HACK race condition -- somehow you can get fragments put into DataStore, which aren't full adverts?? Seen Oct 2019
	if ( ! Advert.isa(item)) {
		const oldItem = item;
		// const base = Object.assign(item);
		// base.id = advertId; // make sure we have an ID!
		// // make sure we have an advertiser!
		// item = new Advert(base);
		// DataStore.setData({type:C.TYPES.Advert, id:advertId, status:C.KStatus.DRAFT}, item, false);
		console.warn("AdvertPage", "item is NOT an advert?!", oldItem);
		return <><h1>Advert: {advertId}</h1><Misc.Loading /></>;
	}

	Advert.assIsa(item);
	setTaskTags(C.TYPES.Advert, advertId, item.vertiser);
	return <>
		<div className="p-3">
			<h2>Advert: {item.name}</h2>
			<div>
				<span>ID: <code>{item.id}</code> <CopyButton size="xs" text={item.id} /></span>
				<span className="ml-4">Created: {item.created ? dateStr(new Date(item.created)) : '(date unknown)'}</span>
				<span className="ml-4">Last modified: {item.created ? dateStr(new Date(item.lastModified)) : '(date unknown)'}</span>
			</div>
		</div>
		<EditAdvertForm advert={item} />
	</>;
} // ./AdvertPage


function EditAdvertForm({ advert }) {
	Advert.assIsa(advert);
	if (!advert.id) {
		// worrying
		console.warn("Advert without an ID?!", advert);
		advert.id = C.newId;
	}
	assert(advert.id, advert);
	Advert.assIsa(advert);
	// a new advert?
	if (!advert.vertiser) {
		if (advert.id === C.newId) {
			const tid = getNavContext(C.TYPES.Advertiser);
			const urlv = DataStore.getUrlValue(C.NAV.vertiser);
			if (urlv && tid) assert(urlv===tid, "Advertiser mismatch: nav-context: "+tid+" vs url-param: "+urlv);
			advert.vertiser = tid || urlv;
		} else {
			// NB: can happen temporarily if editing who owns this advert
			console.warn("Advert without a .vertiser?!", advert);
		}
	}

	// HACK fill in Advertiser's name
	if (!advert.vertiserName && advert.vertiser) {
		const pvAdvertiser = getDataItem({ status: KStatus.PUBLISHED, type: C.TYPES.Advertiser, id: advert.vertiser, swallow: true });
		if (pvAdvertiser.value) {
			advert.vertiserName = pvAdvertiser.value.name;
		}
	}

	const path = DataStore.getDataPath({ status: KStatus.DRAFT, type: C.TYPES.Advert, id: advert.id });

	const workflowNum = asNum(DataStore.getUrlValue("workflow")) || 0;
	const workflow = WORKFLOW_STEPS[workflowNum];

	const advanced = DataStore.getUrlValue('advanced');

	let pubCol;
	if (isPublished(advert)) {
		pubCol = getStatus(advert) === KStatus.PUBLISHED ? "success" : "warning";
	} else {
		pubCol = null;
	}
	const isSocial = (KAdFormat.social === advert.format);

	return (<Editor3ColLayout>
		<LeftSidebar>
			<div style={{ width: '17rem' }}>
				<AdvertWizardChecklist advert={advert} workflow={workflow} workflowNum={workflowNum} />
			</div>
		</LeftSidebar>
		<MainPane className="form-compact">
			{!advanced && <StyleBlock>{`.advanced {display:none;}`}</StyleBlock>}
			<Wizard stagePath={['location', 'params', 'workflow']} navPosition="both" >
				<WizardStage title={titleForWorkflow(KWorkflow.Goal)} canJumpAhead complete={isStartComplete(advert)} sufficient={!!advert.name} >
					<GoalControls path={path} advert={advert} />
				</WizardStage>
				<WizardStage navPosition="top" title={titleForWorkflow(KWorkflow.DesignAd, advert)} canJumpAhead>
					<AdDesignControls path={path} advert={advert} />
				</WizardStage>
				<WizardStage title={titleForWorkflow(KWorkflow.DesignPostEngagement, advert)} canJumpAhead>
					<EndCardDesignControls path={path} advert={advert} />
				</WizardStage>
				<WizardStage title={titleForWorkflow(KWorkflow.Publish, advert)} canJumpAhead color={pubCol}>
					<ExportControls path={path} advert={advert} />
				</WizardStage>
				<WizardStage title='Advanced Settings' canJumpAhead>
					<AdPreviewCard advert={advert} adid={advert.id} path={path} advanced />
					<AdDesignExtraControls path={path} advert={advert} />
				</WizardStage>
			</Wizard>

			<SavePublishDeleteEtc type={C.TYPES.Advert} id={advert.id} hidden sendDiff />

			{/* Also save the Campaign once we can */
				workflowNum && advert.campaign ? (
					<SavePublishDeleteEtc type={C.TYPES.Campaign} id={advert.campaign} hidden sendDiff />
				) : null
			}

		</MainPane>
		{/* <RightSidebar><Sidebar /><ChatController /></RightSidebar> */}
	</Editor3ColLayout>);
}

/**
 * Check for a published version on the server. This _should_ be covered by item.status, but that's not 100% reliable.
 * @param {DataItem} item
 * @returns {Boolean}
 */
const isPublished = (item) => {
	if (!item) return false;
	const id = getId(item);
	const type = getType(item);
	if (!id || !type) {
		return false;
	}
	let pvPub = getDataItem({ type, id, status: KStatus.PUBLISHED, swallow: true });
	if (pvPub.resolved) {
		return !!pvPub.value;
	}
	let status = getStatus(item);
	return status === KStatus.PUBLISHED || status === KStatus.MODIFIED;
};


function Sidebar({ }) {
	const sz = getScreenSize();
	if (sz !== 'xl' && sz !== 'lg') return null;
	return <div>{Roles.isDev() && false && "ChatWidget?"}</div>;
}




/** 
 * Mark current, historical, and mock adverts
 */
export function ServingBadge({ serving, hasServed }) {
	const servingClass = serving ? 'serving now-serving badge badge-success' : (
		hasServed ? 'serving has-served badge badge-primary' : 'serving never-served badge badge-secondary'
	);
	const servingIcon = serving ? 'play' : (
		hasServed ? 'pause' : 'minus'
	);
	const servingDesc = serving ? 'Serving Now' : (
		hasServed ? 'Previously Served' : 'Never Served'
	);
	return (
		<div className={servingClass} title={servingDesc}>
			<Misc.Icon fa={servingIcon} />
			<span className="serving-desc"> {servingDesc}</span>
		</div>
	);
}


function ListItem(props) {
  return <>
	<DefaultListItem {...props} extraDetail={props.item.campaign && "Campaign: " + props.item.campaign} />
	<ServingBadge serving={props.item.serving} hasServed={props.item.hasServed} />
</>;
};


function ListAdverts() {

	const type = C.TYPES.Advert;

	return (
		<>
			<p>
				To create the initial Advert for a campaign: Request a mock from the monday.com <LinkOut href="https://good-loop.monday.com/boards/2643467896">Deals board</LinkOut><br/>
				For extra Adverts in the same campaign: Pick the initial one, then use <i>Save As</i> to make a copy.<br />
                MP4 mirrors of historic VAST creatives can be found <a href="/vast-mirrors/" target="_blank">here</a>.
			</p>
			<ListItems type={type} filterWith={[C.TYPES.Advertiser,C.TYPES.Agency]} 
				ListItem={ListItem} 
				FilterControls={AdvertFilterControls}
				canCreate={isDebug()}
				createWith={[C.TYPES.Advertiser]}
				filterFn={(ad) => ad.format !== "display"}
			/>

		</>
	);
}


function AdvertFilterControls() {

	const onChangeFormat = e => {
		const sq = new SearchQuery(DataStore.getUrlValue("q"));
		const sq2 = SearchQuery.setProp(sq, "format", e.value);
		DataStore.setUrlValue("q", sq2.query);
	};
	const onChangeServed = e => {
		const sq = new SearchQuery(DataStore.getUrlValue("q"));
		const served = e.value;
		let hasServed = null; let serving = null;
		switch (served) {
		case "all": break;
		case "Serving":
			serving = true; break;
		case "Has Served":
			hasServed = true; break;
		case "Not Served":
			serving = false;
			hasServed = false; break;
		}
		let sq2 = SearchQuery.setProp(sq, "hasServed", hasServed);
		sq2 = SearchQuery.setProp(sq2, "serving", serving);
		DataStore.setUrlValue("q", sq2.query);
	};

	return <Form inline>
		<PropControl type="search" prop="filter" label="Filter" />
		<PropControl className="ml-3" type="select" prop="format"
			label="Format"
			options={[""].concat(KAdFormat.values)}
			labels={["any"]}
			saveFn={onChangeFormat}
		/>
		<PropControl type="select" className="ml-3" prop="served" label="Served" 
			options={['all', 'Serving', 'Has Served', 'Not Served']}
			saveFn={onChangeServed}
		/>
	</Form>;
}



export default AdvertPage;
